/**
 * ============================================================
 * AdminPeople.js — Manage People (Admin Panel)
 * ============================================================
 *
 * Loads:
 *   - List of people from backend (GET /api/people/all)
 *
 * Features:
 *   - Search by first/last name or email
 *   - Click any row to edit a person
 *   - "Add Person" button -> /admin/people/new
 *
 * Navigation:
 *   - Uses SynlabAdminNavbar for consistent admin navigation
 *   - Uses shared AdminSubpage.css for layout
 **/

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import SearchBar from "../components/SearchBar";
import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminPeople() {

  // Component state
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Validate token on initial page load
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        await authFetch("/api/authenticate");
        // If token is valid, nothing happens
      } catch (err) {
        // authFetch already handles redirect for 401/403
        console.error("Token validation failed:", err);
      }
    }
    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Load people after token is confirmed valid
  // ------------------------------------------------------------
  useEffect(() => {
    async function fetchPeople() {
      try {
        const res = await authFetch("/api/people/all");
        if (!res) return; // redirected on invalid token

        const data = await res.json();

        // Make sure data is an array
        if (Array.isArray(data)) {
          setPeople(data);
        } else {
          console.warn("People API returned non-array: ", data);
          setPeople([]); // fallback to empty list
        }

      } catch (err) {
        console.error("AdminPeople fetch error:", err);
        setPeople([]); // fallback instead of crashing
      }
    }

    fetchPeople();
  }, []);

  // ------------------------------------------------------------
  // Filtered people list
  // ------------------------------------------------------------
  const filteredPeople = useMemo(() => {
    const s = search.toLowerCase();

    // Check that people is an array
    const list = Array.isArray(people) ? people : [];

    return list.filter((p) =>
      p.firstname.toLowerCase().includes(s) ||
      p.lastname.toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s)
    );
  }, [search, people]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        {/* ----- PAGE TITLE + CREATE BUTTON ----- */}
        <div className="admin-page-header">
          <h1 className="admin-page-title">Manage People</h1>

          <Button
            className="admin-add-button"
            onClick={() => navigate("/admin/people/new")}
          >
            + Add Person
          </Button>
        </div>

        {/* ----- PAGE DESCRIPTION ----- */}
        <p className="admin-page-description">
          Search, view, and edit all Synlab people records.
        </p>
        
        {/* ----- SEARCH BAR ----- */}
        <div className="admin-searchbar-wrapper">
          <SearchBar
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ----- PEOPLE TABLE IS EMPTY ----- */}
        {people.length === 0 && search === "" && (
          <div className="admin-empty-state">
            <p>No people found.</p>
          </div>
        )}

        {/* ----- RESULTS TABLE ----- */}
        {people.length > 0 && (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                </tr>
              </thead>

              <tbody>
                {(search ? filteredPeople : people).map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/admin/people/${p.id}`)}
                    className="admin-table-row"
                  >
                    <td>{p.firstname}</td>
                    <td>{p.lastname}</td>
                    <td>{p.email}</td>
                  </tr>
                ))}

                {(search && filteredPeople.length === 0) && (
                  <tr>
                    <td colSpan="3">No people match your search</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* ----- FOOTER ----- */}
      <SynlabAdminFooter />

    </div>
  );
}

export default AdminPeople;